import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ScrollTimeline.css';

gsap.registerPlugin(ScrollTrigger);

function ScrollTimeline({ events }) {
    const timelineRef = useRef(null);
    const pathRef = useRef(null);

    const handleResumeDownload = () => {
        const link = document.createElement('a');
        link.href = '/Pragyanshu-Tiwari-Resume.pdf';
        link.download = 'Pragyanshu-Tiwari-Resume.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        const path = pathRef.current;
        const pathLength = path.getTotalLength();

        // Set initial state for path animation
        path.style.strokeDasharray = pathLength;
        path.style.strokeDashoffset = pathLength;

        const scrollTriggers = [];

        // Animate the timeline path on scroll
        const pathTrigger = ScrollTrigger.create({
            trigger: timelineRef.current,
            start: 'top center',
            end: 'bottom center',
            scrub: 1,
            animation: gsap.to(path, {
                strokeDashoffset: 0,
                ease: 'none',
            }),
        });
        scrollTriggers.push(pathTrigger);

        // Animate timeline dots
        const dots = document.querySelectorAll('.timeline-dot');
        dots.forEach((dot, index) => {
            // Position dots on the curve - use the curveX from events data
            let xPosition;
            if (events[index]?.curveX) {
                xPosition = events[index].curveX + 50; // Add 50px offset to account for SVG shift
            } else {
                // Default positions that match the SVG curve
                const defaultPositions = [275, 275, 290, 330];
                xPosition = defaultPositions[index] || 275;
            }
            dot.style.left = `${xPosition}px`;

            gsap.set(dot, { scale: 0, opacity: 0 });

            const dotTrigger = ScrollTrigger.create({
                trigger: dot,
                start: 'top 80%',
                end: 'top 60%',
                scrub: 1,
                animation: gsap.to(dot, {
                    scale: 1,
                    opacity: 1,
                    ease: 'none',
                }),
            });
            scrollTriggers.push(dotTrigger);
        });

        // Animate years
        const years = document.querySelectorAll('.timeline-year');
        years.forEach((year) => {
            gsap.set(year, { opacity: 0, x: -30 });

            const yearTrigger = ScrollTrigger.create({
                trigger: year,
                start: 'top 80%',
                end: 'top 60%',
                scrub: 1,
                animation: gsap.to(year, {
                    opacity: 1,
                    x: 0,
                    ease: 'none',
                }),
            });
            scrollTriggers.push(yearTrigger);
        });

        // Animate content
        const contents = document.querySelectorAll('.timeline-content');
        contents.forEach((content) => {
            gsap.set(content, { opacity: 0, y: 30 });

            const contentTrigger = ScrollTrigger.create({
                trigger: content,
                start: 'top 80%',
                end: 'top 60%',
                scrub: 1,
                animation: gsap.to(content, {
                    opacity: 1,
                    y: 0,
                    ease: 'none',
                }),
            });
            scrollTriggers.push(contentTrigger);
        });

        // Animate images
        const images = document.querySelectorAll('.timeline-image');
        images.forEach((image) => {
            gsap.set(image, { opacity: 0, scale: 0.8, y: 50, rotation: -5 });

            const imageTrigger = ScrollTrigger.create({
                trigger: image,
                start: 'top 85%',
                end: 'top 55%',
                scrub: 1,
                animation: gsap.to(image, {
                    opacity: 1,
                    scale: 1,
                    y: 0,
                    rotation: 0,
                    ease: 'none',
                }),
            });
            scrollTriggers.push(imageTrigger);
        });

        // Animate resume button
        const resumeButton = document.querySelector('.resume-download-btn');
        if (resumeButton) {
            gsap.set(resumeButton, { opacity: 0, scale: 0.5, rotation: -10 });

            const resumeTrigger = ScrollTrigger.create({
                trigger: resumeButton,
                start: 'top 90%',
                end: 'top 70%',
                scrub: 1,
                animation: gsap.to(resumeButton, {
                    opacity: 1,
                    scale: 1,
                    rotation: 0,
                    ease: 'none',
                }),
            });
            scrollTriggers.push(resumeTrigger);
        }

        // Animate achievement counters
        const achievementNumbers = document.querySelectorAll('.achievement-number');
        achievementNumbers.forEach((number) => {
            const finalValue = number.textContent;
            const hasPlus = finalValue.includes('+');
            const numericValue = parseInt(finalValue.replace('+', ''));

            // Set initial value to 0
            number.textContent = '0';

            const counterTrigger = ScrollTrigger.create({
                trigger: number,
                start: 'top 85%',
                onEnter: () => {
                    // Animate counter from 0 to final value
                    gsap.to({ val: 0 }, {
                        val: numericValue,
                        duration: 2,
                        ease: 'power2.out',
                        onUpdate: function () {
                            const currentVal = Math.floor(this.targets()[0].val);
                            number.textContent = hasPlus ? currentVal + '+' : currentVal;
                        }
                    });
                },
                once: true // Only animate once
            });
            scrollTriggers.push(counterTrigger);
        });

        // Cleanup
        return () => {
            scrollTriggers.forEach(trigger => trigger.kill());
        };
    }, [events]);

    return (
        <div className="scroll-timeline" ref={timelineRef}>
            {/* Timeline SVG Path */}
            <svg className="timeline-svg" viewBox="0 0 500 900" preserveAspectRatio="none">
                <path
                    ref={pathRef}
                    d="M 10 0 Q 120 150, 75 300 Q 40 450, 90 600 Q 130 750, 75 800"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="7"
                />
            </svg>

            {/* Timeline Events */}
            <div className="timeline-events">
                {events.map((event, index) => (
                    <div key={index} className="timeline-item" style={{ top: `${event.position}px` }}>
                        <div className="timeline-year">{event.year}</div>
                        <div className={`timeline-dot ${event.isPresent ? 'present-dot' : ''}`}></div>
                        <div className="timeline-content">
                            <h3 className="timeline-title">{event.title}</h3>
                            <p className="timeline-description" dangerouslySetInnerHTML={{ __html: event.description }}></p>
                        </div>
                        {event.image && (
                            <img src={event.image} alt={event.title} className="timeline-image" />
                        )}
                    </div>
                ))}
            </div>

            {/* Resume Download Button - positioned at blue circle location */}
            <div className="resume-endpoint" style={{ top: '1150px', left: '200px' }}>
                <button className="resume-download-btn" onClick={handleResumeDownload}>
                    <div className="resume-circle">
                        {/* SVG for rotating text - mathematically perfect circle */}
                        <svg className="rotating-text" viewBox="0 0 140 140" width="140" height="140">
                            <defs>
                                <path
                                    id="circle-path"
                                    d="M 70,10 
                                       A 60,60 0 0,1 130,70 
                                       A 60,60 0 0,1 70,130 
                                       A 60,60 0 0,1 10,70 
                                       A 60,60 0 0,1 70,10 Z"
                                    fill="none"
                                />
                            </defs>
                            <text fontSize="10" fill="rgba(255, 107, 53, 0.8)" letterSpacing="3.5">
                                <textPath href="#circle-path" startOffset="0" method="align" spacing="auto">
                                    DOWNLOAD • RESUME • ⭐ • DOWNLOAD • RESUME • 📄 •
                                </textPath>
                                <animateTransform
                                    attributeName="transform"
                                    attributeType="XML"
                                    type="rotate"
                                    from="0 70 70"
                                    to="360 70 70"
                                    dur="20s"
                                    repeatCount="indefinite"
                                />
                            </text>
                        </svg>
                        <div className="resume-arrow">↗</div>
                    </div>
                    <div className="resume-text">
                        <span className="resume-action">DOWNLOAD</span>
                        <span className="resume-label">RESUME</span>
                    </div>
                </button>
            </div >

            {/* Achievements - Left side */}
            <div className="achievements" style={{ position: 'absolute', top: '1100px', left: '50px' }}>
                <div className="achievement-item">
                    <span className="achievement-number">2</span>
                    <span className="achievement-text">Paid Internships</span>
                </div>
                <div className="achievement-item">
                    <span className="achievement-number">10+</span>
                    <span className="achievement-text">Projects</span>
                </div>
                <div className="achievement-item">
                    <span className="achievement-number">3</span>
                    <span className="achievement-text">NPM Packages Published</span>
                </div>
            </div>

            {/* Social Links - Right side */}
            <div className="social-links" style={{ position: 'absolute', top: '1100px', right: '50px' }}>
                <a href="https://www.linkedin.com/in/pragyanshutiwari/" target="_blank" rel="noopener noreferrer" className="social-link" data-tooltip="LinkedIn">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                </a>

                <a href="https://github.com/Pragyanshu08" target="_blank" rel="noopener noreferrer" className="social-link" data-tooltip="GitHub">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                    </svg>
                </a>
                <a href="mailto:yashtiwari4000@gmail.com" className="social-link" data-tooltip="Email">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M0 3v18h24v-18h-24zm6.623 7.929l-4.623 5.712v-9.458l4.623 3.746zm-4.141-5.929h19.035l-9.517 7.713-9.518-7.713zm5.694 7.188l3.824 3.099 3.83-3.104 5.612 6.817h-18.779l5.513-6.812zm9.208-1.264l4.616-3.741v9.348l-4.616-5.607z" />
                    </svg>
                </a>
                <a href="https://instagram.com/yashtiwari888" target="_blank" rel="noopener noreferrer" className="social-link" data-tooltip="Instagram">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                </a>
            </div>
        </div >
    );
}

export default ScrollTimeline;